import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    IconButton,
    Fab,
    Tooltip,
    Alert,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SecurityIcon from '@mui/icons-material/Security';
import GiteIcon from '@mui/icons-material/Gite';
import LoginIcon from '@mui/icons-material/Login';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SensorsIcon from '@mui/icons-material/Sensors';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import TourIcon from '@mui/icons-material/Tour';
import LightModeIcon from '@mui/icons-material/LightMode';
import SettingsIcon from '@mui/icons-material/Settings';
import CableIcon from '@mui/icons-material/Cable';
import NotificationsIcon from '@mui/icons-material/Notifications';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DiamondIcon from '@mui/icons-material/Diamond';
import ComputerIcon from '@mui/icons-material/Computer';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EventIcon from '@mui/icons-material/Event';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import WarningIcon from '@mui/icons-material/Warning';
import FlightIcon from '@mui/icons-material/Flight';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import BadgeIcon from '@mui/icons-material/Badge';
import eventsApi from '../../../api/eventsApi/eventsApi';
import inspectionReportsApi, {
    InspectionReportAnswerDto,
    InspectionReportDetailDto,
} from '../../../api/eventsApi/inspectionReportsApi';
import ChecklistModal from '../../home/components/checklistModal';

function isMeaningfulReportAnswer(a: InspectionReportAnswerDto): boolean {
    if (a.yesNo === true || a.yesNo === false) return true;
    if ((a.description || '').trim().length > 0) return true;
    if ((a.findings || '').trim().length > 0) return true;
    if ((a.proposedSolutions || '').trim().length > 0) return true;
    return false;
}


interface QuestionCategory {
    id: string;
    name: string;
    isDisabled: boolean;
    index: number | null;
}

interface Question {
    id: string;
    text: string;
    questionCategory: QuestionCategory;
    answers: unknown[];
}

const ICON_SIZE = { fontSize: 24 };

const CATEGORY_ICON_KEYWORDS: [string[], React.ReactNode][] = [
    [['tesis profili', 'genel bilgiler'], <BusinessIcon sx={ICON_SIZE} />],
    [['yakın dış çevre', 'dış çevre analizi'], <LocationOnIcon sx={ICON_SIZE} />],
    [['çevre güvenliği', 'çevre ve sınır', 'sınır güvenliği', 'yerleşke'], <SecurityIcon sx={ICON_SIZE} />],
    [['güvenlik kontrol', 'tarama uygulama'], <SecurityIcon sx={ICON_SIZE} />],
    [['tesis fiziki', 'sabit nokta'], <GiteIcon sx={ICON_SIZE} />],
    [['giriş-çıkış', 'giriş çıkış', 'giriş yetkilendirme'], <LoginIcon sx={ICON_SIZE} />],
    [['araç giriş', 'otopark', 'araç girişleri'], <DirectionsCarIcon sx={ICON_SIZE} />],
    [['güvenlik sistem', 'cctv', 'kamera'], <SensorsIcon sx={ICON_SIZE} />],
    [['ziyaretçi'], <PersonIcon sx={ICON_SIZE} />],
    [['güvenlik kadro', 'personel güvenliği'], <GroupsIcon sx={ICON_SIZE} />],
    [['silahlı güvenlik'], <LocalPoliceIcon sx={ICON_SIZE} />],
    [['devriye'], <TourIcon sx={ICON_SIZE} />],
    [['aydınlatma'], <LightModeIcon sx={ICON_SIZE} />],
    [['operasyonel', 'idari', 'hizmet alan', 'otorite'], <SettingsIcon sx={ICON_SIZE} />],
    [['entegre', 'elektronik güvenlik'], <CableIcon sx={ICON_SIZE} />],
    [['iletişim', 'haberleşme', 'alarm', 'acil durum', 'doğal afet'], <NotificationsIcon sx={ICON_SIZE} />],
    [['üretim', 'depolama'], <InventoryIcon sx={ICON_SIZE} />],
    [['mal kabul', 'kargo', 'lojistik', 'posta'], <LocalShippingIcon sx={ICON_SIZE} />],
    [['kritik varlık', 'kayıp önleme', 'ekonomik değer'], <DiamondIcon sx={ICON_SIZE} />],
    [['bilgi güvenliği'], <ComputerIcon sx={ICON_SIZE} />],
    [['insan faktörü', 'sosyal risk'], <PsychologyIcon sx={ICON_SIZE} />],
    [['etkinlik', 'vip', 'özel gün'], <EventIcon sx={ICON_SIZE} />],
    [['ilave tesis'], <AddBusinessIcon sx={ICON_SIZE} />],
    [['risk ve tehdit', 'risk değerlendirme'], <AnalyticsIcon sx={ICON_SIZE} />],
    [['koruma', 'güvenlik planı'], <MiscellaneousServicesIcon sx={ICON_SIZE} />],
    [['tesis personeli', 'personel'], <BadgeIcon sx={ICON_SIZE} />],
    [['drone', 'iha', 'yeni nesil tehdit'], <FlightIcon sx={ICON_SIZE} />],
    [['acil durum', 'kriz yönetimi'], <WarningIcon sx={ICON_SIZE} />],
];

export const getCategoryIcon = (categoryName: string): React.ReactNode => {
    const n = categoryName.toLocaleLowerCase('tr-TR').trim();
    for (const [keywords, icon] of CATEGORY_ICON_KEYWORDS) {
        if (keywords.some((kw) => n.includes(kw))) return icon;
    }
    return <AssignmentIcon sx={ICON_SIZE} />;
};

const ChecklistPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const reportId = searchParams.get('reportId') || undefined;
    const isSavedPdfChecklistView = searchParams.get('view') === 'savedPdf';

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [pinName, setPinName] = useState('');
    const [reportTitle, setReportTitle] = useState<string | null>(null);
    const [reportStatus, setReportStatus] = useState<string | null>(null);
    const [restrictQuestionIds, setRestrictQuestionIds] = useState<string[] | null>(null);
    const [categories, setCategories] = useState<QuestionCategory[]>([]);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [categoryQuestionCounts, setCategoryQuestionCounts] = useState<Record<string, number>>({});
    const [categoryAnsweredCounts, setCategoryAnsweredCounts] = useState<Record<string, number>>({});
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);

    const inspectionReadOnly = reportStatus === 'Completed' || reportStatus === 'Archived';
    const allowCompletedFindingsEdit = isSavedPdfChecklistView;

    const loadData = useCallback(async (showLoader = false) => {
        if (!id) return;
        try {
            if (showLoader) setLoading(true);
            setLoadError(null);
            const [pinInfo, response] = await Promise.all([
                eventsApi.getPinInfo(id),
                eventsApi.getPinQuestionsAndAnswers(id),
            ]);

            const name = (pinInfo as { name?: string; Name?: string }).name
                ?? (pinInfo as { name?: string; Name?: string }).Name ?? '';
            setPinName(name);

            let qList = response.questions as Question[];
            let restrict: string[] | null = null;
            let inspectionReport: InspectionReportDetailDto | null = null;

            if (reportId) {
                inspectionReport = await inspectionReportsApi.getById(reportId);
                if (inspectionReport.pinId !== id) {
                    setLoadError('Bu rapor bu tesise ait değil.');
                    setRestrictQuestionIds(null);
                    setCategories([]);
                    setQuestions([]);
                    return;
                }
                setReportTitle(inspectionReport.title || null);
                setReportStatus(inspectionReport.status);
                const answeredIds = inspectionReport.answers
                    .filter(isMeaningfulReportAnswer)
                    .map((a) => a.questionId);
                restrict = answeredIds;
                const rq = new Set(answeredIds);
                qList = qList.filter((q) => rq.has(q.id));
            } else {
                setReportTitle(null);
                setReportStatus(null);
                setRestrictQuestionIds(null);
            }

            if (reportId) {
                setRestrictQuestionIds(restrict);
            }

            const categoryMap = new Map<string, QuestionCategory>();
            const counts: Record<string, number> = {};
            const answered: Record<string, number> = {};

            for (const q of qList) {
                const cat = q.questionCategory;
                if (!categoryMap.has(cat.id)) categoryMap.set(cat.id, cat);
                counts[cat.id] = (counts[cat.id] || 0) + 1;
                if (reportId) {
                    answered[cat.id] = (answered[cat.id] || 0) + 1;
                } else if (q.answers?.length) {
                    answered[cat.id] = (answered[cat.id] || 0) + 1;
                }
            }

            if (reportId && inspectionReport?.riskAnswers?.length) {
                const dedup = new Map<string, (typeof inspectionReport.riskAnswers)[number]>();
                for (const ra of inspectionReport.riskAnswers) {
                    if (!dedup.has(ra.questionCategoryId)) dedup.set(ra.questionCategoryId, ra);
                }
                const fullQL = response.questions as Question[];
                for (const ra of dedup.values()) {
                    const hasInFilteredList = qList.some((q) => q.questionCategory.id === ra.questionCategoryId);
                    if (hasInFilteredList) continue;
                    if (categoryMap.has(ra.questionCategoryId)) continue;
                    const live = fullQL.find((q) => q.questionCategory.id === ra.questionCategoryId);
                    const cat: QuestionCategory = live
                        ? live.questionCategory
                        : {
                              id: ra.questionCategoryId,
                              name: ra.questionCategoryName?.trim()
                                  ? ra.questionCategoryName
                                  : 'Kategori',
                              isDisabled: false,
                              index: null,
                          };
                    categoryMap.set(cat.id, cat);
                    counts[cat.id] = 1;
                    answered[cat.id] = 1;
                }
            }

            const unique = Array.from(categoryMap.values()).filter(
                (c) => c.name !== 'SONUÇ VE DEĞERLENDİRME'
            );
            setCategories([...unique].sort((a, b) => (a.index ?? 999) - (b.index ?? 999)));
            setQuestions(qList);
            setCategoryQuestionCounts(counts);
            setCategoryAnsweredCounts(answered);
        } catch (err) {
            console.error('ChecklistPage fetch error:', err);
            setLoadError('Veriler yüklenemedi.');
        } finally {
            if (showLoader) setLoading(false);
        }
    }, [id, reportId]);

    useEffect(() => { loadData(true); }, [loadData]);

    const totalQuestions = useMemo(() => {
        if (!reportId) return questions.length;
        return Object.values(categoryQuestionCounts).reduce((a, n) => a + n, 0);
    }, [reportId, questions.length, categoryQuestionCounts]);
    const totalAnswered = Object.values(categoryAnsweredCounts).reduce((a, b) => a + b, 0);

    const filteredCategories = useMemo(() => {
        if (!searchQuery.trim()) return categories;
        const q = searchQuery.trim().toLocaleLowerCase('tr-TR');

        return categories.filter((category) => {
            const categoryMatches = category.name.toLocaleLowerCase('tr-TR').includes(q);
            if (categoryMatches) return true;

            return questions.some(
                (question) =>
                    question.questionCategory.id === category.id &&
                    question.text.toLocaleLowerCase('tr-TR').includes(q)
            );
        });
    }, [categories, questions, searchQuery]);

    const handleBack = () => navigate(`/reports/${id}`);
    const handleSummary = () => navigate(`/reports/${id}/checklist/summary`);
    const handleAddBulgu = () => navigate(`/reports/${id}/checklist/bulgu`);

    if (!id) return null;

    return (
        <Box sx={{ 
            height: '100vh', 
            display: 'flex', flexDirection: 'column', 
            bgcolor: '#EDF4F6', overflow: 'hidden',
        }}>
            {/* Header */}
            <Box sx={{ bgcolor: '#0D3D4A', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <IconButton onClick={handleBack} sx={{ color: 'white', p: 1.5, minWidth: 48, minHeight: 48, borderRadius: 0 }}>
                    <ArrowBackIcon sx={{ fontSize: 24 }} />
                </IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 10, color: '#7FAAB8', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                        {isSavedPdfChecklistView ? 'KAYITLI RAPOR — CHECKLIST' : reportId ? 'DENETİM RAPORU' : 'DENETİM'}
                    </Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'white', fontFamily: 'Inter, sans-serif' }}>
                        {reportTitle || pinName || 'Risk Analiz Formu'}
                    </Typography>
                    {reportId && reportStatus && (
                        <Typography sx={{ fontSize: 11, color: '#B8D4DE', mt: 0.25 }}>
                            Durum: {reportStatus}
                            {inspectionReadOnly
                                ? allowCompletedFindingsEdit
                                    ? ' · Bulgu/öneri düzenlenebilir'
                                    : ' · Salt okunur (düzenlemek için raporu yeniden açın)'
                                : ''}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 0, px: 1.5, py: 0.8, display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'white', lineHeight: 1 }}>
                            {totalAnswered}/{totalQuestions}
                        </Typography>
                    </Box>
                    {!reportId && (
                        <Box
                            component="button"
                            onClick={handleAddBulgu}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 0.5,
                                bgcolor: 'rgba(255,255,255,0.95)', color: '#0D3D4A', border: 'none',
                                borderRadius: 0, px: 1.5, py: 0.8,
                                cursor: 'pointer', fontWeight: 800,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                transition: 'all 0.15s',
                                '&:hover': { bgcolor: '#FFFFFF', transform: 'scale(1.03)', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }
                            }}
                        >
                            <AddIcon sx={{ fontSize: 18 }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0D3D4A', lineHeight: 1, whiteSpace: 'nowrap' }}>
                                Bulgu Ekle
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, pb: 6 }}>
                {loadError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {loadError}
                    </Alert>
                )}
                {reportId && !loadError && totalQuestions === 0 && !loading && (
                    <Alert
                        severity="info"
                        icon={false}
                        sx={{
                            mb: 2,
                            bgcolor: '#0D3D4A',
                            color: '#fff',
                            '& .MuiAlert-message': { color: '#fff', width: '100%' },
                        }}
                    >
                        Bu raporda görüntülenecek cevaplanmış soru bulunamadı.
                    </Alert>
                )}
                <TextField
                    fullWidth
                    placeholder="Ana başlık veya soru ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#5A7A84', fontSize: 24 }} />
                            </InputAdornment>
                        ),
                        sx: {
                            bgcolor: 'white',
                            borderRadius: '4px',
                            minHeight: 52,
                            fontSize: 16,
                            '& fieldset': { border: '2px solid #C8DDE4' },
                            '&:hover fieldset': { borderColor: '#1A6070' },
                        },
                    }}
                    sx={{ mb: 1.5 }}
                />

                {/* Color legend */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, px: 0.5 }}>
                    <Tooltip
                        title={
                            <Box sx={{ p: 0.5 }}>
                                <Typography sx={{ fontSize: 12, mb: 0.5 }}><strong>Beyaz:</strong> Henüz cevaplanmadı</Typography>
                                <Typography sx={{ fontSize: 12, mb: 0.5 }}><strong style={{ color: '#1FA35C' }}>Yeşil:</strong> Bulgu yok, sorun yok</Typography>
                                <Typography sx={{ fontSize: 12, mb: 0.5 }}><strong style={{ color: '#E67E22' }}>Turuncu:</strong> Okudum, cevaba gerek yok</Typography>
                                <Typography sx={{ fontSize: 12 }}><strong style={{ color: '#D93B2B' }}>Kırmızı:</strong> Bulgu var</Typography>
                            </Box>
                        }
                        arrow
                        placement="bottom-start"
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: '#5A7A84' }} />
                            <Typography sx={{ fontSize: 11, color: '#5A7A84' }}>Renk bilgisi</Typography>
                        </Box>
                    </Tooltip>
                    <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
                        {[
                            { color: '#C8DDE4', label: 'Cevapsız' },
                            { color: '#1FA35C', label: 'Bulgu yok' },
                            { color: '#E67E22', label: 'Gerek yok' },
                            { color: '#D93B2B', label: 'Bulgu var' },
                        ].map((item) => (
                            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
                                <Typography sx={{ fontSize: 10, color: '#5A7A84' }}>{item.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: '#0D3D4A' }} />
                    </Box>
                ) : (
                    filteredCategories.map((category) => {
                        const total = categoryQuestionCounts[category.id] || 0;
                        const answered = categoryAnsweredCounts[category.id] || 0;
                        const isExpanded = expandedCategoryId === category.id;

                        return (
                            <Accordion
                                key={category.id}
                                expanded={isExpanded}
                                onChange={(_, expanded) => setExpandedCategoryId(expanded ? category.id : null)}
                                sx={{
                                    mb: 1.5,
                                    borderRadius: '4px !important',
                                    overflow: 'hidden',
                                    '&:before': { display: 'none' },
                                    border: 'none',
                                    boxShadow: 'none',
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: '#5A7A84', fontSize: 28 }} />}
                                    sx={{
                                        bgcolor: 'white',
                                        minHeight: 64,
                                        '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2 },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 44, height: 44, borderRadius: '4px',
                                            bgcolor: 'rgba(13,61,74,0.08)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: '#0D3D4A',
                                        }}
                                    >
                                        {getCategoryIcon(category.name)}
                                    </Box>
                                    <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 15, color: '#0D1B1E' }}>
                                        {category.name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 50, height: 4, bgcolor: '#E4EFF3', borderRadius: 2, overflow: 'hidden' }}>
                                            <Box
                                                sx={{
                                                    width: `${total ? (answered / total) * 100 : 0}%`,
                                                    height: '100%',
                                                    bgcolor: answered === total && total > 0 ? '#1FA35C' : '#D93B2B',
                                                    borderRadius: 2,
                                                }}
                                            />
                                        </Box>
                                        <Typography sx={{ fontSize: 11, color: '#5A7A84', fontFamily: 'monospace' }}>
                                            {answered}/{total}
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0, bgcolor: '#F2F7F9', borderTop: 'none' }}>
                                    {isExpanded && (
                                        <ChecklistModal
                                            open={true}
                                            onClose={() => setExpandedCategoryId(null)}
                                            pinId={id!}
                                            embedded
                                            initialCategoryId={category.id}
                                            onAnswersSaved={() => loadData(false)}
                                            inspectionReportId={reportId}
                                            inspectionReadOnly={inspectionReadOnly}
                                            allowCompletedFindingsEdit={allowCompletedFindingsEdit}
                                            restrictQuestionIds={reportId ? restrictQuestionIds : null}
                                        />
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })
                )}
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    bgcolor: '#F2F7F9', borderTop: '1px solid #C8DDE4',
                    p: 2, display: 'flex', gap: 2, zIndex: 40,
                }}
            >
                <Box
                    component="button"
                    onClick={handleBack}
                    sx={{
                        minWidth: 56, minHeight: 56,
                        border: '2px solid #C8DDE4', borderRadius: 0,
                        color: '#0D3D4A', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', bgcolor: 'white',
                        '&:hover': { borderColor: '#0D3D4A', bgcolor: 'rgba(13,61,74,0.04)' },
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box
                    component="button"
                    onClick={reportId ? undefined : handleSummary}
                    sx={{
                        flex: 1, minHeight: 56, borderRadius: 0, border: 'none',
                        bgcolor: reportId ? '#B0BEC5' : '#0D3D4A',
                        color: 'white', fontWeight: 700, fontSize: 17,
                        cursor: reportId ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: reportId ? 0.85 : 1,
                        '&:hover': { bgcolor: reportId ? '#B0BEC5' : '#134F60' },
                    }}
                >
                    {reportId ? 'Özet (yalnızca canlı denetim)' : 'Özete Git →'}
                </Box>
            </Box>
        </Box>
    );
};

export default ChecklistPage;
